import React, { ReactNode, useMemo } from 'react';
import styled from '@emotion/styled';
import {
    InputLabel,
    MenuItem,
    Select as SelectMUI,
    FormControl as FormControlMaterial,
    FormHelperText,
    Checkbox,
    ListItemText,
    Chip,
    Input,
    Autocomplete,
    TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { expandProps, FormControlBaseProps, FormControlProps, DisplayProps, ExtraProps } from './FormControlProps';
import { Dictionaries, useDictionaries } from '../../hooks/useDictionaries';
import { FieldSchema } from '../../hooks/useSchema';
import { FormattedMessage, useIntl } from 'react-intl';
import { Hint } from './Hint';
import { toMap } from '../../api/data';

interface SelectProps {
    values: { value: any, label: ReactNode, disabled?: boolean }[];
    value: any;
    controlProps?: any;
    onChange: (v: any) => void;
    error?: boolean;
    label?: ReactNode;
    hint?: ReactNode;
}

export const SelectControl = (props: SelectProps) => {
    const { label, hint, value, controlProps, values, onChange, error } = props;
    return (
        <FormControlMaterial style={{width: '100%'}} size="small" error={error} required={controlProps?.required}>
            <InputLabel>{label}</InputLabel>
            <SelectMUI
                value={value === null || value === "undefined" ? "" : value}
                {...controlProps}
                onChange={e => onChange(e.target.value)}>
                {(values || []).concat([{ value: null, label: "-"}]).map(({ value, label, disabled }) => (
                    <MenuItem key={value || 'undef'} value={value} disabled={disabled}>
                        {label}
                    </MenuItem>
                ))}
            </SelectMUI>
            {hint && <FormHelperText>{hint}</FormHelperText>}
        </FormControlMaterial>);
}

export const Select = (props: FormControlProps) => {
    const { field, row, label, value, schema, controlProps, extraProps, onChange, error } = expandProps(props);
    
    if(extraProps?.autoComplete) {
      return <AutocompleteCombobox {...props} />
    }

    const showValues = extraProps?.showValues;
    const labelIdPrefix = extraProps?.labelIdPrefix;

    return (
        <FormControlMaterial style={{width: '100%'}} size="small" error={error} required={controlProps?.required}>
            <InputLabel>{label}</InputLabel>
            <SelectMUI
                value={value || ""}
                {...controlProps}
                onChange={e => onChange(row, { [field]: e.target.value })}>
                {(schema.values || []).concat([{ value: null, label: extraProps?.emptyLabel || "-"}]).map(({ value, label, disabled }) => (
                    <MenuItem key={value || 'undef'} value={value} disabled={disabled}>
                        {showValues ?
                            value :
                            labelIdPrefix ? (value ? <FormattedMessage id={`${labelIdPrefix}.${value}`} /> : label)
                            : label}
                    </MenuItem>
                ))}
            </SelectMUI>
            <Hint schema={schema} extraProps={extraProps} />
        </FormControlMaterial>);
}



const useOptionsLabels = (schema: FieldSchema, extraProps: ExtraProps | undefined) => {
  const { formatMessage } = useIntl();

  const showValues = extraProps?.showValues;
  const labelIdPrefix = extraProps?.labelIdPrefix;


  const [options, getLabel, getDisabled] = useMemo(() => {
    const optionsDict = toMap(schema.values || [], r => r.value, r => r);
    let gl: (v: string | null) => string = v => v || "";
    if(showValues) {
      // pass
    } else if(labelIdPrefix) {
      gl = v => v ? formatMessage({ id: `${labelIdPrefix}.${v}` }) || "" : "";
    } else if(schema.valueDict) {
      gl = v => v ? schema.valueDict[v] || "" : "";
    } else {
      gl = v => v ? optionsDict[v]?.label : "";
    }

    return [
      (schema.values || []).map(v => v.value),
      gl,
      (v: string | null) => v ? (optionsDict[v]?.disabled || false) : false,
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema.values, schema.valueDict, labelIdPrefix, showValues]);

  return {
    options,
    getLabel,
    getDisabled,
  }
}

export const AutocompleteCombobox = (props: FormControlProps) => {
  const { field, row, value, label, schema, controlProps, extraProps, onChange, error } = expandProps(props);

  const { formatMessage } = useIntl();
  const { options, getLabel, getDisabled } = useOptionsLabels(schema, extraProps);

  return (
    <Autocomplete
      // disablePortal
      autoHighlight
      fullWidth
      size="small"
      options={options}
      value={value || ""}
      onChange={(e,newValue) => { onChange(row, { [field]: newValue }); }}
      getOptionLabel={getLabel}
      getOptionDisabled={getDisabled}
      noOptionsText={formatMessage({ id: "common.no_options" })}
      renderInput={(params) => <TextField error={!!error} {...controlProps} {...params} size="small" margin="none" label={label} />}
      disabled={extraProps?.disabled}
      readOnly={extraProps?.readOnly}
      />
  );
}


export const AutocompleteComboboxMulti = (props: FormControlProps) => {
  const { field, row, value, label, schema, controlProps, extraProps, onChange, error } = expandProps(props);

  const { formatMessage } = useIntl();
  const { options, getLabel, getDisabled } = useOptionsLabels(schema, extraProps);

  return (
    <Autocomplete
      // disablePortal
      multiple
      autoHighlight
      fullWidth
      size="small"
      options={options}
      value={value || []}
      onChange={(e,newValue) => { onChange(row, { [field]: newValue }); }}
      getOptionLabel={getLabel}
      getOptionDisabled={getDisabled}
      noOptionsText={formatMessage({ id: "common.no_options" })}
      renderInput={(params) => <TextField error={!!error} {...controlProps} {...params} size="small" margin="none" label={label} />}
      renderTags={extraProps?.replaceWithCountAfter && extraProps.replaceWithCountAfter <= (value?.length || 0)
        ? x => <FormattedMessage id="common.selected_n" values={{ value: x.length || 0}} />
        : undefined
      }
      />
  );
}



export const adjustDictionarySelectSchema = (schema: FieldSchema, dictionaries: Dictionaries): FieldSchema => {
    if(!schema?.dictionary) {
        return schema;
    } else {
        const thisDict = dictionaries[schema.dictionary];
        return { ...schema, values: thisDict.values, valueDict: thisDict.valueDict };
    }
}

const adjustDictionarySelectProps = <T extends FormControlBaseProps>(props: T, dictionaries: Dictionaries): T => {
    const { schema } = props;
    const newSchema = adjustDictionarySelectSchema(schema, dictionaries);

    return { ...props, schema: newSchema };
}


export const DictionarySelect = (props: FormControlProps) => {
    const dictionaries = useDictionaries();
    const adjustedProps = adjustDictionarySelectProps(props, dictionaries);
    return <Select {...adjustedProps} />
}

export const SelectDisplay = ({ schema, row, field, config }: DisplayProps) => {
    const value = row[field];
    const labelIdPrefix = config?.labelIdPrefix;
    return <>{labelIdPrefix && value ? <FormattedMessage id={`${labelIdPrefix}.${value}`} /> : ((!!schema.valueDict && schema.valueDict[value]) || (!value && config?.emptyLabel) || value)}</>;
}

export const DictionarySelectDisplay = (props: DisplayProps) => {
    const dictionaries = useDictionaries();
    return <SelectDisplay {...adjustDictionarySelectProps(props, dictionaries)} />    
}

export const SelectMultiDisplay = ({ schema, row, field, config }: DisplayProps) => {
    const value = row[field] as string[];
    return <>{(value || []).map(v => schema.valueDict[v] || v).join(", ") || config?.emptyLabel || ""}</>;
}

export const DictionarySelectMultiDisplay = (props: DisplayProps) => {
    const dictionaries = useDictionaries();
    return <SelectMultiDisplay {...adjustDictionarySelectProps(props, dictionaries)} />    
}


export const useMultiStyle = makeStyles({
    inputId: {},
    labelId: {}
});

export const SelectChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

export const SelectChip = styled(Chip)`
  margin: 2px;
`;

type DictionarySelectMultiType = (props: FormControlProps) => JSX.Element

export const DictionarySelectMulti: DictionarySelectMultiType = (props) => {
    const dictionaries = useDictionaries();
    return <MultiSelect {...adjustDictionarySelectProps(props, dictionaries)} />
}


type MultiSelectType = (props: FormControlProps) => JSX.Element

export const MultiSelect: MultiSelectType = (props) => {
    const { inputId, labelId } = useMultiStyle();
    const { field, row, label, value, schema, controlProps, extraProps, error, onChange } = expandProps(props);
    const { formatMessage } = useIntl();

    const selected = (value || []) as any[];

    const options = schema.values || [];
    const labelIdPrefix = extraProps?.labelIdPrefix;

    if(extraProps?.autoComplete) {
      return <AutocompleteComboboxMulti {...props} />
    }

    return <FormControlMaterial fullWidth size="small" required={controlProps?.required}>
        <InputLabel id={labelId}>{label}</InputLabel>
        <SelectMUI
            id={inputId}
            labelId={labelId}
            multiple
            error={error}
            {...controlProps}
            value={selected}
            onChange={e => onChange(row, { [field]: e.target.value})}
            input={<Input />}
            renderValue={_ => extraProps?.multiSelectValueDisplay === "text"
                ? options
                  .filter(({value}) => selected.includes(value))
                  .map(({value, label}) => labelIdPrefix && value ? formatMessage({ id: `${labelIdPrefix}.${value}` }) : label)
                  .join(", ")
                : (
                  <SelectChipContainer>
                    {options
                      .filter(({value}) => selected.includes(value))
                      .map(({value, label}) => 
                        <SelectChip
                          key={value || 'undef'}
                          label={labelIdPrefix ? (value ? <FormattedMessage id={`${labelIdPrefix}.${value}`} /> : label) : label} />)
                    }
                  </SelectChipContainer>
            )}
        >
            {options.map(({ value, label, disabled }) => (
                <MenuItem onClick={e => e.preventDefault()} key={value || 'undef'} value={value} disabled={disabled}>
                    <Checkbox checked={selected.includes(value)} />
                    <ListItemText primary={labelIdPrefix ? (value ? <FormattedMessage id={`${labelIdPrefix}.${value}`} /> : label) : label} />
                </MenuItem>
            ))}
        </SelectMUI>
        <Hint schema={schema} extraProps={extraProps} />
    </FormControlMaterial>
}
